import { roundTo } from '../../../utils/formula/evaluateExpression.js'

/**
 * A line whose amount is typed as a total, gross or net, rather than as a unit
 * price — a receipt says what was paid, not what one unit cost before VAT.
 *
 * The two typed fields sit behind one dualInput (G/N), so at most one of them
 * is non-zero. The line's amounts follow from whichever it is:
 *
 *   gross typed   net = gross / (1 + rate), VAT = gross − net
 *   net typed     net as typed,             VAT = net × rate
 *   priced        net = unit price × quantity, VAT = net × rate
 *
 * VAT is taken as the difference on the gross side so the line adds back up to
 * exactly what was typed. When an amount is typed, the unit price is derived
 * from it, so it still reads correctly on the document.
 *
 * A priced line — a product's cost, a line copied from a purchase order, or a
 * line with nothing typed — shows its net in the net field too, so the amount
 * box is never blank, and follows the quantity. `pricedField` remembers the net
 * this operation wrote there: while the net field still holds it, the line is
 * priced and is recalculated; once the user types a different net, it is
 * theirs and stays as typed. Picking a product writes its cost to the unit
 * price, the net field and `pricedField` at once, which is what marks it priced.
 *
 * Config:
 *   subEntity                              the lines array
 *   grossField, netField                   the two typed fields
 *   pricedField                            the net last written by pricing
 *   rateField, quantityField               VAT rate (0.25) and quantity
 *   unitPriceField                         read when priced, derived otherwise
 *   targetNetField, targetTaxField, targetGrossField
 */
const subGrossNet = (data, operation) => {
    const {
        subEntity, grossField, netField, pricedField, rateField, quantityField, unitPriceField,
        targetNetField, targetTaxField, targetGrossField
    } = operation

    if (!data || !Array.isArray(data[subEntity])) {
        return data
    }

    const newSubArray = data[subEntity].map(elem => {
        const gross = Number(elem[grossField]) || 0
        const shownNet = roundTo(Number(elem[netField]) || 0)
        const priced = roundTo(Number(elem[pricedField]) || 0)
        const rate = Number(elem[rateField]) || 0
        const quantity = Number(elem[quantityField]) || 0
        const unitPrice = Number(elem[unitPriceField]) || 0

        const mode = gross !== 0 ? 'gross'
            : (shownNet !== 0 && shownNet !== priced) ? 'net'
            : 'priced'

        let net, tax
        const changes = {}

        if (mode === 'gross') {
            net = roundTo(gross / (1 + rate))
            tax = roundTo(gross - net)
        } else if (mode === 'net') {
            net = shownNet
            tax = roundTo(net * rate)
        } else {
            net = roundTo(unitPrice * quantity)
            tax = roundTo(net * rate)
            changes[netField] = net
        }

        changes[pricedField] = mode === 'priced' ? net : 0
        if (mode !== 'priced' && quantity !== 0) {
            changes[unitPriceField] = roundTo(net / quantity)
        }

        return {
            ...elem,
            ...changes,
            [targetNetField]: net,
            [targetTaxField]: tax,
            [targetGrossField]: roundTo(net + tax)
        }
    })

    return {
        ...data,
        [subEntity]: newSubArray
    }
}

export default subGrossNet
